/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { inject, observer } from 'mobx-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { useTranslations } from 'next-intl';
import CloseOneIcon from '../svg/CloseOneIcon';
import Note2Icon from '../svg/Note2Icon';
import ArrowTwoIcon from '../svg/ArrowTwoIcon';
import { DynamicProduct } from '../dynamic-imports';
import ProductImage from './ProductImage';
import Tags from "./Tags";
import Price from "./Price";
import AddToCart from './AddToCart';
import MobilePrice from './mobile/MobilePrice';
import MobilePriceInfo from './mobile/MobilePriceInfo';
import 'swiper/css';
import wooCommerceStore from '@/stores/woocommerceStore';
import { sanitize, hexToRgba, rgbaToHex } from '@/utils/misc';
import { trackViewItem } from '@/utils/tracking';

const ProductModalView = inject('studioStore')(observer(({
  studioStore,
  product,
  img,
  selectedVariation,
  setSelectedVariation,
  productType,
  productDisplayType,
  setProductDisplayType,
  handleRemoveFromCart,
  handleAddToCart,
  handleUpdateQuantity,
  productWeightQuantity,
  quantity,
  handleVariationChange,
}) => {
  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color;
  const upsellBackgroundColor = studioStore.settings.product_popup?.upsell_background_color;
  const primaryColor = studioStore.settings.colors.primary_color;
  const lighterPrimaryColor = rgbaToHex(hexToRgba(primaryColor, 0.2, 'object'), 'rgb');
  const [activeTab, setActiveTab] = useState(0);
  const [upsellProducts, setUpsellProducts] = useState([]);
  const [crosssellProducts, setCrosssellProducts] = useState([]);
  const [isUpsellLoading, setIsUpsellLoading] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState('');
  const t = useTranslations('Cart');
  const tp = useTranslations('Product');
  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl md:mx-auto max-h-[90vh] z-50";
  let tabClasses = "tab-item inline-block cursor-pointer text-primary-studio p-4 me-2 font-secondary";

  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    tabClasses = tabClasses + " stamp-border stamp-border-xs";
    style.backgroundImage = `radial-gradient(8px, rgba(255, 255, 255, 0) 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  const getTabsData = () => {
    const tabs = [];
    const metaData = product.meta_data || [];

    metaData.forEach(meta => {
      const key = meta.key;

      if (key.startsWith('tabs_') && key.includes('_title')) {
        const index = key.match(/\d+/)[0]; // Get the index of the tab (e.g., "0", "1")
        const title = meta.value;
        // Only add the tab if it has a title
        if (title && title.trim()) {
          const textMeta = metaData.find(item => item.key === `tabs_${index}_text`);
          const text = textMeta ? textMeta.value : '';

          tabs.push({
            title,
            text: sanitize(text),
          });
        }
      }
    });

    return tabs;
  };

  const tabs = getTabsData();

  // Load existing note when modal opens
  useEffect(() => {
    const existingItem = wooCommerceStore.cart.find(item => item.id === product.id);
    if (existingItem?.note) {
      setNote(existingItem.note);
    }
  }, [product.id]);

  // Fetch upsell products when the modal is opened
  useEffect(() => {
    const fetchUpsellProducts = async () => {
      if (product.upsell_ids.length > 0) {
        setIsUpsellLoading(true);
        try {
          const response = await axios.get('/api/products', {
            params: {
              include: product.upsell_ids.join(','),
            },
          });
          setUpsellProducts(response.data.products);
          setCrosssellProducts(response.data.crossSellProducts);
        } catch (error) {
          console.error("Error fetching upsell products:", error);
        } finally {
          setIsUpsellLoading(false);
        }
      }
    };

    fetchUpsellProducts();
  }, [product]);

  useEffect(() => {
    // Track view_item event when the modal opens
    trackViewItem(product);
  }, [product]);

  const handleSaveNote = () => {
    wooCommerceStore.updateCartItemNote(product.id, note);
    setShowNoteInput(false);
  };

  // Wrap the original handleAddToCart to include the note
  const handleAddToCartWithNote = () => {
    const success = handleAddToCart();
    if (success) {
      // Always update the note if it exists, regardless of whether it was just added or already existed
      if (note) {
        wooCommerceStore.updateCartItemNote(product.id, note);
      }
    }
  };

  return (
    <div className={`${classes}`} style={style}>
      <button
          className="absolute top-4 md:top-5 left-4 md:left-5 rtl: right-4 rtl:md:right-5 rtl:left-auto z-10"
          onClick={() => wooCommerceStore.hideProductModal()}
        >
        <CloseOneIcon size="28px" stroke={primaryColor} />
      </button>
      <div className="max-h-[86vh] overflow-y-auto">
        <div className="p-3 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 px-4 md:px-12">
            <ProductImage img={img} productName={product?.name} width={380} height={380} />

            {/* Product Info */}
            <div className="py-6 text-center md:text-start lg:pe-20 2xl:pe-30 2xl:py-6 2xl:ps-6">
              <Tags product={product} side="regular" size="large" />
              <h2 className="mt-3 mb-2 text-2xl font-semibold truncate text-heading text-primary-studio">
                {product?.name}
              </h2>
              <div
                className={`text-secondary-text-studio${product?.description ?? ' mb-4'}`}
                dangerouslySetInnerHTML={{ __html: sanitize(product?.description ?? '') }}
              />

              {/* Price and Add to Cart */}
              <div className="mb-4 hidden md:block">
                <Price
                  product={selectedVariation || product}
                  productType={productType}
                  productDisplayType={productDisplayType}
                  setProductDisplayType={setProductDisplayType}
                />
              </div>
              <div className="mb-4 block md:hidden">
                <MobilePrice
                  product={selectedVariation || product}
                  productType={productType}
                  setProductDisplayType={setProductDisplayType}
                />
              </div>
              <hr className="dotted-spaced my-5" />
              <div className="mb-4 block md:hidden">
                <MobilePriceInfo
                  product={selectedVariation || product}
                  productType={productType}
                  setProductDisplayType={setProductDisplayType}
                />
              </div>
              {/* Quantity Controls */}
              <AddToCart
                product={product}
                handleRemoveFromCart={handleRemoveFromCart}
                handleAddToCart={handleAddToCartWithNote}
                handleUpdateQuantity={handleUpdateQuantity}
                quantity={quantity}
                productWeightQuantity={productWeightQuantity}
                quantityController="modal"
                handleVariationChange={handleVariationChange}
                productType={productType}
                selectedVariation={selectedVariation}
                setSelectedVariation={setSelectedVariation}
                productDisplayType={productDisplayType}
                layout="horizontal"
                isModal
              />
              <hr className="dotted-spaced my-5" />
              {/* Note Input */}
              <div className="mt-4">
                <div className="flex items-center text-sm cursor-pointer text-primary-studio" onClick={() => setShowNoteInput(!showNoteInput)}>
                  <Note2Icon className="w-6 h-6 me-1" />
                  {note ? note : t('add_note')}
                </div>
                {showNoteInput && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="w-full text-sm p-2 border rounded"
                      placeholder={t('enter_note')}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveNote()}
                      onBlur={handleSaveNote}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {tabs.length > 0 && (
            <div className="product-tabs">
              <hr className="dotted-spaced" />
              <div className="overflow-x-auto no-scrollbar">
                <ul className="flex whitespace-nowrap p-2 space-x-4">
                  {tabs.map((tab, index) => (
                    <li
                      key={index}
                      className={`${tabClasses} ${index === activeTab ? 'active' : ''}`}
                      onClick={() => setActiveTab(index)}
                    >
                      {tab.title}
                    </li>
                  ))}
                </ul>
              </div>
              <hr className="dotted-spaced" />
              <div className="page-content px-4 md:px-12 mt-4">
                <p>{tabs[activeTab].text}</p>
              </div>
            </div>
          )}
        </div>
        {/* Upsell Products Carousel */}
        {upsellProducts.length > 0 && (
          <div className="upsell p-2 md:p-6 mb-2 relative" style={{backgroundColor: upsellBackgroundColor}}>
            <div className="upsell-products mt-8 relative">
              <h3 className="text-xl font-secondary mb-8 text-primary-studio px-3 md:px-12">{tp("upsell_title")}</h3>

              {/* Mobile List View */}
              <div className="md:hidden">
                <div className="space-y-4 px-3">
                  {upsellProducts.map((upsell, index) => (
                    <div key={index}>
                      <DynamicProduct product={upsell} crossSellProducts={crosssellProducts} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop Carousel View */}
              <div className="hidden md:block px-12">
                {/* Custom navigation buttons */}
                <div className="swiper-button-prev custom-nav-btn absolute left-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer w-10 h-10 flex items-center justify-center">
                  <ArrowTwoIcon width="20px" fill={primaryColor} direction="left" />
                </div>
                <div className="swiper-button-next custom-nav-btn absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer w-10 h-10 flex items-center justify-center">
                  <ArrowTwoIcon width="20px" fill={primaryColor} direction="right" />
                </div>

                <Swiper
                  spaceBetween={20}
                  slidesPerView={2}
                  navigation={{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                  }}
                  modules={[Navigation]}
                  breakpoints={{
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 20,
                    },
                    1330: {
                      slidesPerView: 4,
                      spaceBetween: 20,
                    },
                  }}
                >
                  {upsellProducts.map((upsell, index) => (
                    <SwiperSlide key={index}>
                      <DynamicProduct product={upsell} crossSellProducts={crosssellProducts} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        )}

        {isUpsellLoading && <p>{tp("loading_upsell_products")}</p>}
      </div>
      <style jsx global>{`
        /* Navigation buttons styles */
        .custom-nav-btn {
          transition: opacity 0.2s ease-in-out;
        }

        .custom-nav-btn:hover {
          opacity: 0.8;
        }

        .custom-nav-btn.swiper-button-disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .swiper-button-next::after,
        .swiper-button-prev::after {
          content: none !important;
        }

        /* Tab styles */
        .tab-item.stamp-border.stamp-border-xs {
          padding: 4px 10px 5px;
        }
        ${container === "stamp" ? `
          .tab-item.stamp-border.stamp-border-xs:hover,
          .tab-item.stamp-border.stamp-border-xs.active {
            background-image: radial-gradient(2px, rgba(255, 255, 255, 0) 98%, ${lighterPrimaryColor}),
            linear-gradient(${lighterPrimaryColor} 0px, ${lighterPrimaryColor} 0px);
            padding: 4px 10px 5px;
          }
        ` : `
          .tab-item:hover,
          .active {
            background-color: ${lighterPrimaryColor};
            color: ${primaryColor};
          }
        `}
      `}</style>
    </div>
  );
}));

export default ProductModalView;
